/**
 * The Trenches developer console — the main site's rail, the Uniswap-simple
 * console inside it. Four destinations and nothing external: API (keys and
 * quick start), Docs, Support, Account — everything a subscriber needs lives
 * on this page, so nobody bounces between two sites. Outbound links exist
 * only as inline details (the issues page, legal), never as navigation.
 *
 * Trenches' own skin throughout: Departure Mono display type, the gold
 * brand, square corners, dark always, no visible scrollbars. Keys render
 * masked and copy real.
 */

import { useEffect, useRef, useState } from "react";

import "./index.css";

type KeyInfo = { key: string; expires: number; plan?: string; name?: string };
type Me = {
  providers: { github: boolean; google: boolean };
  who?: string;
  email?: string;
  provider?: "github" | "google";
  plan?: string;
  stripe?: boolean;
  billing?: boolean;
  keys?: KeyInfo[];
};

/** What Pro includes, and the only number the usage bar is a fraction of. */
const PRO_REQUESTS = 10_000_000;

const HOST = location.hostname === "localhost" ? "rpc.trenches.sh" : location.hostname;

/** The ▚ mark: two equal squares, quadrant to quadrant. */
function Mark({ size = 20 }: { size?: number }) {
  return (
    <svg viewBox="0 0 16 16" width={size} height={size} shapeRendering="crispEdges" aria-hidden>
      <rect x="4" y="1" width="4" height="7" className="fill-brand" />
      <rect x="8" y="8" width="4" height="7" className="fill-brand" />
    </svg>
  );
}

function GitHubIcon({ size = 15 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  );
}

function GoogleIcon({ size = 15 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
      <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden>
      <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
    </svg>
  );
}

const BTN_PRIMARY =
  "inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-sm bg-foreground px-4 text-sm font-semibold text-background transition-colors hover:bg-brand disabled:pointer-events-none disabled:opacity-40";
const BTN_OUTLINE =
  "inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2.5 rounded-sm border border-border px-4 text-sm font-medium transition-colors hover:border-brand hover:text-brand disabled:pointer-events-none disabled:opacity-40";

// ---- login: two halves, one line between them -----------------------------
// The left half is solid dark and speaks in display type — the name, huge,
// and one line of fun. The right half keeps the dot grid and holds the two
// buttons. A single border separates them; the contrast is the design.

function LoginPage({ me }: { me: Me | null }) {
  const p = me?.providers ?? { github: false, google: false };
  return (
    // One column on a phone — brand, then card, one uninterrupted surface;
    // the split and its dividing line belong to screens wide enough for two.
    <div className="flex min-h-svh flex-col lg:flex-row">
      <div className="flex items-center justify-center bg-background px-8 pt-14 pb-8 lg:flex-1 lg:border-r lg:border-border lg:py-14">
        <div className="flex max-w-[560px] flex-col items-center gap-6 text-center lg:items-start lg:gap-7 lg:text-left">
          <span className="lg:hidden">
            <Mark size={88} />
          </span>
          <span className="hidden lg:block">
            <Mark size={160} />
          </span>
          <h1 className="font-display text-[clamp(52px,7vw,96px)] leading-[0.92]">
            Trenches
            <br />
            <span className="text-brand">RPC</span>
          </h1>
          <p className="text-xl text-muted-foreground md:text-2xl">Ape into the next runner.</p>
        </div>
      </div>

      <div className="dotgrid flex flex-1 items-start justify-center px-6 pt-2 pb-14 lg:items-center lg:py-14">
        <div className="w-full max-w-sm rounded-sm border border-border bg-card p-8">
          <h2 className="pb-6 text-center text-lg">Log in or sign up</h2>
          <div className="flex flex-col gap-2.5">
            <button className={BTN_OUTLINE} disabled={!p.google} onClick={() => (location.href = "/auth/google")}>
              <GoogleIcon /> Continue with Google
            </button>
            <button className={BTN_OUTLINE} disabled={!p.github} onClick={() => (location.href = "/auth/github")}>
              <GitHubIcon /> Continue with GitHub
            </button>
          </div>
          <p className="pt-6 text-center text-xs text-balance text-muted-foreground">
            By continuing you agree to the{" "}
            <a
              className="underline underline-offset-4 hover:text-brand"
              href="https://trenches.sh/terms"
              target="_blank"
              rel="noreferrer"
            >
              Terms
            </a>{" "}
            and{" "}
            <a
              className="underline underline-offset-4 hover:text-brand"
              href="https://trenches.sh/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

// ---- console --------------------------------------------------------------

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return "Up late";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

/** One snippet per copy — the key masked on screen, real on the clipboard. */
function Snippet({ real }: { real: string }) {
  const [done, setDone] = useState(false);
  const masked = real.replace(/trk_[A-Za-z0-9]+/g, "trk_…");
  return (
    <div className="relative rounded-sm border border-border bg-background">
      <pre className="scroll-thin overflow-x-auto p-3 pr-12 text-[13px] leading-6 select-none">{masked}</pre>
      <button
        className={`absolute top-2 right-2 flex size-7 cursor-pointer items-center justify-center rounded-sm border text-xs transition-colors ${
          done ? "border-good text-good" : "border-border hover:border-brand hover:text-brand"
        }`}
        aria-label="Copy endpoint"
        onClick={async () => {
          await navigator.clipboard.writeText(real);
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        }}
      >
        {done ? "✓" : "⧉"}
      </button>
    </div>
  );
}

/** The whole config, key threaded through — one paste instead of four. */
function configFor(k: string): string {
  return JSON.stringify(
    {
      // Editors that understand it get completion and validation on every
      // field, which is worth one line in a file people edit by hand.
      $schema: "https://trenches.sh/config.schema.json",
      networks: [
        {
          name: "solana-mainnet",
          kind: "solana",
          rpc: `https://${HOST}/${k}/solana`,
          ws: `wss://${HOST}/${k}/solana-ws`,
        },
        {
          name: "robinhood-mainnet",
          chain_id: 4663,
          rpc: `https://${HOST}/${k}/robinhood`,
          // Solana's websocket was here and Robinhood's was not, so anyone
          // pasting this got a terminal that polled for EVM launches. Polling
          // cannot see every launch: the log window is capped, and blocks
          // skipped to keep up with the head are never read again.
          ws: `wss://${HOST}/${k}/robinhood-ws`,
        },
      ],
      // Top level, not per network: risk scoring is about a token, not a
      // chain. Pointing it here means the higher rate limit applies without
      // anyone holding the RugCheck key — the Worker attaches it.
      rugcheck: { base_url: `https://${HOST}/${k}/rugcheck` },
    },
    null,
    2,
  );
}

function EndpointTabs({ k }: { k: string }) {
  const tabs = [
    { id: "config", lines: [configFor(k)] },
    { id: "solana", lines: [`https://${HOST}/${k}/solana`, `wss://${HOST}/${k}/solana-ws`] },
    { id: "robinhood", lines: [`https://${HOST}/${k}/robinhood`, `wss://${HOST}/${k}/robinhood-ws`] },
    { id: "base", lines: [`https://${HOST}/${k}/base`, `wss://${HOST}/${k}/base-ws`] },
    // Not a chain: a REST proxy to RugCheck with the key attached server-side,
    // so the higher rate limit is available without publishing the key.
    { id: "rugcheck", lines: [`https://${HOST}/${k}/rugcheck`] },
  ];
  const [tab, setTab] = useState("config");
  const active = tabs.find((t) => t.id === tab)!;
  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex gap-5 border-b border-border text-sm">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`cursor-pointer pb-2.5 transition-colors ${
              tab === t.id
                ? "-mb-px border-b-2 border-brand text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.id}
          </button>
        ))}
      </div>
      {active.lines.map((l) => (
        <Snippet key={l} real={l} />
      ))}
    </div>
  );
}

function KeyRow({ k, me, patch }: { k: KeyInfo; me: Me; patch: (fn: (m: Me) => Me) => void }) {
  const [done, setDone] = useState(false);
  const [menu, setMenu] = useState(false);
  const short = `${k.key.slice(0, 8)}…${k.key.slice(-4)}`;

  // Every mutation updates the list from ITS OWN response. Refetching
  // /portal/me straight after a write raced KV's edge cache — the stale
  // read wore the old name, and a rename looked lost the moment it saved.
  const rename = async () => {
    const name = prompt("Name this key", k.name ?? `${me.who}'s key`);
    if (name === null) return;
    const r = await fetch("/portal/keys/rename", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ key: k.key, name }),
    });
    const body = await r.json().catch(() => ({}));
    if (r.ok)
      patch((m) => ({
        ...m,
        keys: m.keys?.map((x) => (x.key === k.key ? { ...x, name: body.name } : x)),
      }));
  };

  const del = async () => {
    if (!confirm("Delete this key? Its endpoints stop working immediately.")) return;
    const r = await fetch("/portal/keys/delete", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ key: k.key }),
    });
    if (r.ok) patch((m) => ({ ...m, keys: m.keys?.filter((x) => x.key !== k.key) }));
  };

  const rotate = async () => {
    if (!confirm("Rotate this key? The old key stops working immediately and new endpoints take its place.")) return;
    const r = await fetch("/portal/keys/rotate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ key: k.key }),
    });
    const body = await r.json().catch(() => ({}));
    if (r.ok && body.rotated)
      patch((m) => ({
        ...m,
        keys: m.keys?.map((x) => (x.key === k.key ? { ...x, key: body.rotated } : x)),
      }));
  };

  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-4 px-4 py-3.5 sm:grid-cols-[1.4fr_1.2fr_0.8fr_5rem]">
      <div className="flex min-w-0 items-center gap-3">
        <Mark size={16} />
        <button
          className="cursor-pointer truncate text-left text-sm transition-colors hover:text-brand"
          title="Rename"
          onClick={rename}
        >
          {k.name ?? `${me.who}'s key`}
        </button>
      </div>
      <button
        className="group flex cursor-pointer items-center gap-2 justify-self-start text-[13px] text-muted-foreground transition-colors hover:text-brand"
        aria-label="Copy API key"
        onClick={async () => {
          await navigator.clipboard.writeText(k.key);
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        }}
      >
        <span className="select-none">{done ? "copied" : short}</span>
        <span className={done ? "text-good" : "opacity-60 group-hover:opacity-100"}>{done ? "✓" : "⧉"}</span>
      </button>
      <span className="hidden text-[13px] sm:block">
        {k.plan === "free" ? (
          <span className="text-muted-foreground">Free · 10 RPS</span>
        ) : (
          <span className="text-brand">Pro</span>
        )}
      </span>

      <span className="relative justify-self-end">
        <button
          className="cursor-pointer rounded-sm px-2 py-1 text-[13px] text-muted-foreground transition-colors hover:bg-card hover:text-brand"
          aria-expanded={menu}
          onClick={() => setMenu((m) => !m)}
        >
          manage
        </button>
        {menu && (
          <>
            <span className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
            <span className="absolute top-8 right-0 z-50 flex w-32 flex-col rounded-sm border border-border bg-card py-1 shadow-xl">
              <button
                className="cursor-pointer px-3 py-1.5 text-left text-[13px] transition-colors hover:bg-background"
                onClick={() => {
                  setMenu(false);
                  rename();
                }}
              >
                Rename
              </button>
              <button
                className="cursor-pointer px-3 py-1.5 text-left text-[13px] transition-colors hover:bg-background"
                onClick={() => {
                  setMenu(false);
                  rotate();
                }}
              >
                Rotate
              </button>
              <button
                className="cursor-pointer px-3 py-1.5 text-left text-[13px] text-bad transition-colors hover:bg-background"
                onClick={() => {
                  setMenu(false);
                  del();
                }}
              >
                Delete
              </button>
            </span>
          </>
        )}
      </span>
    </div>
  );
}

/** The account page — identity, plan, billing, and the two exits. */
function AccountView({ me, patch }: { me: Me; patch: (fn: (m: Me) => Me) => void }) {
  // Identity lives here; money lives in Billing. The name is editable —
  // OAuth's idea of you is a default, not a sentence.
  const rename = async () => {
    const name = prompt("Display name", me.who ?? "");
    if (name === null || !name.trim()) return;
    const r = await fetch("/portal/account/name", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name }),
    });
    const body = await r.json().catch(() => ({}));
    if (r.ok && body.who) patch((m) => ({ ...m, who: body.who }));
  };
  const del = async () => {
    const warn =
      me.plan === "pro"
        ? "You're on Pro. Deleting your account cancels your subscription immediately and your endpoints stop working. Unused time isn't refunded automatically — you can request one from Support."
        : "Delete this account and its keys? The endpoints stop working immediately.";
    if (!confirm(warn)) return;
    const r = await fetch("/portal/account/delete", { method: "POST" });
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      alert(e.error ?? "Deletion failed — your account is untouched. Try again in a moment.");
      return;
    }
    location.href = "/";
  };
  return (
    <div className="flex max-w-xl flex-col gap-8">
      <h1 className="text-2xl">Account</h1>
      <section className="rounded-sm border border-border bg-card p-6">
        <div className="flex items-center gap-4 pb-4">
          <span className="flex size-12 items-center justify-center rounded-sm border border-border bg-background">
            <Mark size={22} />
          </span>
          <div className="min-w-0 flex-1">
            <button
              className="block max-w-full cursor-pointer truncate text-left text-base transition-colors hover:text-brand"
              title="Edit display name"
              onClick={rename}
            >
              {me.who}
            </button>
            {me.email && <p className="truncate text-sm text-muted-foreground">{me.email}</p>}
          </div>
          <span
            className={`rounded-sm border px-2 py-0.5 text-xs ${
              me.plan === "pro" ? "border-brand text-brand" : "border-border text-muted-foreground"
            }`}
          >
            {me.plan === "pro" ? "Pro" : "Free"}
          </span>
        </div>
        {me.provider && (
          <p className="flex items-center gap-2 border-t border-border pt-4 text-sm text-muted-foreground">
            {me.provider === "github" ? <GitHubIcon size={14} /> : <GoogleIcon size={14} />}
            Signed in with {me.provider === "github" ? "GitHub" : "Google"}
          </p>
        )}
      </section>
      <section className="flex flex-col gap-1 rounded-sm border border-border">
        <button
          className="cursor-pointer border-b border-border px-4 py-3 text-left text-sm transition-colors hover:bg-card"
          onClick={() => (location.href = "/auth/logout")}
        >
          Sign out
        </button>
        <button
          className="cursor-pointer px-4 py-3 text-left text-sm text-bad transition-opacity hover:opacity-80"
          onClick={del}
        >
          Delete account
        </button>
      </section>
    </div>
  );
}

/** Billing: what you're on, what Pro replaces, and every money matter in
 *  one room — upgrade, cancellation, refunds. The pitch in one breath: a
 *  subscription instead of a drawer full of per-provider API keys and
 *  their separate bills. */
function BillingView({ me, goSupport }: { me: Me; goSupport: () => void }) {
  const pro = me.plan === "pro";
  const checkout = async () => {
    const r = await fetch("/portal/checkout", { method: "POST" });
    const body = await r.json().catch(() => ({}));
    if (r.ok && body.url) location.href = body.url;
    else alert(`Checkout failed: ${body.error ?? r.status}`);
  };
  const manage = async () => {
    const r = await fetch("/portal/billing", { method: "POST" });
    const body = await r.json().catch(() => ({}));
    if (r.ok && body.url) location.href = body.url;
    else alert(`Billing portal failed: ${body.error ?? r.status}`);
  };
  return (
    <div className="flex max-w-xl flex-col gap-8">
      <h1 className="text-2xl md:text-3xl">
        {greeting()},<br className="md:hidden" /> {(me.who ?? "").split(" ")[0]}
      </h1>

      <section className="flex flex-col gap-1 rounded-sm border border-border bg-card p-6">
        <p className="text-sm text-muted-foreground">Current plan</p>
        <p className={`text-2xl ${pro ? "text-brand" : ""}`}>{pro ? "Pro" : "Free"}</p>
        <p className="pt-1 text-sm text-muted-foreground">
          {pro ? "$119/mo · 10M requests included" : "10 RPS"}
        </p>
      </section>

      {!pro && (
        <section className="flex flex-col gap-4 rounded-sm border border-brand/40 bg-card p-6">
          <h2 className="text-base">
            <span className="text-xl leading-none text-brand">✦</span> Pro RPC endpoint
          </h2>
          <ul className="flex flex-col gap-1.5 text-sm text-muted-foreground">
            <li>· Uncapped rate</li>
            <li>· 10M requests a month</li>
            <li>· HTTP and websockets</li>
            <li>· $5 per million beyond that</li>
          </ul>
          {me.stripe && (
            <button
              className="inline-flex h-10 w-fit cursor-pointer items-center justify-center rounded-sm bg-brand px-6 text-sm font-semibold text-background transition-opacity hover:opacity-90"
              onClick={checkout}
            >
              Upgrade · $119/mo
            </button>
          )}
        </section>
      )}

      {pro && (
        <section className="flex flex-col gap-1 rounded-sm border border-border">
          {me.billing && (
            <button
              className="cursor-pointer border-b border-border px-4 py-3 text-left text-sm transition-colors hover:bg-card"
              onClick={manage}
            >
              Manage subscription
            </button>
          )}
          <button
            className="cursor-pointer px-4 py-3 text-left text-sm transition-colors hover:bg-card"
            onClick={goSupport}
          >
            Request a refund
          </button>
        </section>
      )}
    </div>
  );
}

/** One paste, and the user's own AI does the setup: edits the config,
 *  verifies both endpoints answer, reports back. Works with anything that
 *  can edit files and run curl — Claude Code, Cursor, the lot. */
function agentPrompt(k: string): string {
  return `Set up Trenches RPC in my trading terminal (trenches.sh).

1. Open ~/.config/trenches/config.json. If it doesn't exist, run \`trenches --init\` first (or create the file with a top-level "networks" array).
2. MERGE the following into the networks list — update these fields on the matching entries, keep everything else in the file exactly as it is:
   - entry "solana-mainnet" (kind "solana"):
       "rpc": "https://${HOST}/${k}/solana"
       "ws":  "wss://${HOST}/${k}/solana-ws"
   - entry "robinhood-mainnet" (chain_id 4663):
       "rpc": "https://${HOST}/${k}/robinhood"
       "ws":  "wss://${HOST}/${k}/robinhood-ws"
3. Also set the TOP-LEVEL "rugcheck" key (a sibling of "networks", not inside it):
       "rugcheck": { "base_url": "https://${HOST}/${k}/rugcheck" }
4. Verify the endpoints answer with a JSON result (not an error):
   curl -s https://${HOST}/${k}/solana -H 'content-type: application/json' -d '{"jsonrpc":"2.0","id":1,"method":"getSlot"}'
   curl -s https://${HOST}/${k}/robinhood -H 'content-type: application/json' -d '{"jsonrpc":"2.0","id":1,"method":"eth_blockNumber"}'
   curl -s "https://${HOST}/${k}/rugcheck/v1/tokens/So11111111111111111111111111111111111111112/report/summary"
5. Tell me what you changed and both verification results. The key is a secret: never echo it back in full — write it as trk_… in anything you print.`;
}

function AgentsView({ k }: { k?: string }) {
  const key = k ?? "trk_YOUR_KEY";
  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <div className="flex flex-col gap-3">
        <h1 className="text-2xl">Agents</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Copy this, paste it to your agent — Claude Code, Cursor — and it sets your terminal up: config
          edited, both endpoints verified, nothing overwritten.
        </p>
      </div>
      <Snippet real={agentPrompt(key)} />
    </div>
  );
}

/** In-house docs: everything the endpoints accept, with the reader's own key
 *  already threaded through the examples. */
function DocsView({ k }: { k?: string }) {
  const key = k ?? "trk_YOUR_KEY";
  return (
    <div className="flex max-w-2xl flex-col gap-10">
      <div className="flex flex-col gap-3">
        <h1 className="text-2xl">Docs</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          How to use your RPC endpoints and API keys.
        </p>
      </div>

      <section className="flex flex-col gap-3">
        <h2 className="text-base">Endpoints</h2>
        <Snippet real={`https://${HOST}/${key}/solana`} />
        <Snippet real={`wss://${HOST}/${key}/solana-ws`} />
        <Snippet real={`https://${HOST}/${key}/robinhood`} />
        <p className="text-sm leading-relaxed text-muted-foreground">
          The key can also travel as a query parameter — <code className="text-foreground">?api=KEY</code> and{" "}
          <code className="text-foreground">?api-key=KEY</code> both work:
        </p>
        <Snippet real={`https://${HOST}/solana?api=${key}`} />
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-base">The config</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Paste into <code className="text-foreground">~/.config/trenches/config.json</code> — or merge the networks
          block into yours — and restart the app.
        </p>
        <Snippet real={configFor(key)} />
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base">Keys</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Up to three per account, renamed by clicking their name, rotated or deleted from the API page. The
          subscription lives on the account, not the key — every key inherits the plan, and deleting one never
          touches billing.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base">Limits &amp; pricing</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <span className="text-foreground">Free</span> — 10 requests per second, enough to run the terminal.{" "}
          <span className="text-brand">Pro</span> — $119/month, 10M requests included.
          Uncapped rate, both chains, websockets included.
        </p>
      </section>
    </div>
  );
}

/** Support, in-house — the same channels the site names, without leaving. */
function SupportView() {
  return (
    <div className="flex max-w-2xl flex-col gap-10">
      <div className="flex flex-col gap-3">
        <h1 className="text-2xl">Support</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Here is how to get help, and how to help back.
        </p>
      </div>

      <section className="flex flex-col gap-2">
        <h2 className="text-base">Report a bug</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Open an issue with the build you were running (<code className="text-foreground">trenches --version</code>
          &nbsp;prints it), what you expected, what happened instead, and the session log from{" "}
          <code className="text-foreground">~/.trenches/</code> if it's relevant.
        </p>
        <a
          className="inline-flex items-center gap-2 text-sm text-brand underline underline-offset-4"
          href="https://github.com/asyncswap/trenches/issues"
          target="_blank"
          rel="noreferrer"
        >
          <GitHubIcon size={14} /> github.com/asyncswap/trenches/issues
        </a>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base">Billing &amp; refunds</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Card changes, invoices and cancellation live under Account → Manage subscription. For a refund, email{" "}
          <a className="text-brand underline underline-offset-4" href="mailto:support@asyncswap.org">
            support@asyncswap.org
          </a>
          .
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base">Service status</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The health check answers at{" "}
          <a className="text-brand underline underline-offset-4" href="/health" target="_blank" rel="noreferrer">
            /health
          </a>{" "}
          — if it's green and your endpoint isn't, the problem wants an issue.
        </p>
      </section>
    </div>
  );
}

// ---- the shell: the site's rail, the console inside it --------------------

/** The site's measure, verbatim: the rail line and the right rule, fixed so
 *  both run the full height of every page. */
function Gutters() {
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[5] hidden lg:block">
      <div className="mx-auto flex h-full w-full max-w-[1388px]">
        <div className="w-[236px] shrink-0" />
        <div className="h-full flex-1 border-r border-border" />
      </div>
    </div>
  );
}

type View = "api" | "docs" | "agents" | "billing" | "support" | "account";

const NAV: { id: View; title: string }[] = [
  { id: "api", title: "API" },
  { id: "agents", title: "Agents" },
  { id: "billing", title: "Billing" },
  { id: "docs", title: "Docs" },
  { id: "support", title: "Support" },
  { id: "account", title: "Account" },
];

function MonitorIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

/** The homepage's theme switch: system, light, dark — remembered, and
 *  applied before first paint by a head script so the page never flashes. */
function ThemeToggle() {
  const [mode, setMode] = useState<"system" | "light" | "dark">("system");

  useEffect(() => {
    let saved: string | null = null;
    try {
      saved = localStorage.getItem("theme");
    } catch {}
    if (saved === "light" || saved === "dark") setMode(saved);
  }, []);

  useEffect(() => {
    const dark = mode === "dark" || (mode === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
    document.documentElement.classList.toggle("dark", dark);
    try {
      if (mode === "system") localStorage.removeItem("theme");
      else localStorage.setItem("theme", mode);
    } catch {}
  }, [mode]);

  const opts = [
    { key: "system", Icon: MonitorIcon, label: "System theme" },
    { key: "light", Icon: SunIcon, label: "Light theme" },
    { key: "dark", Icon: MoonIcon, label: "Dark theme" },
  ] as const;

  return (
    <fieldset className="flex items-center gap-1">
      <legend className="sr-only">Theme</legend>
      {opts.map(({ key, Icon, label }) => (
        <button
          key={key}
          type="button"
          aria-label={label}
          aria-pressed={mode === key}
          onClick={() => setMode(key)}
          className={`flex size-7 cursor-pointer items-center justify-center rounded-sm transition-colors ${
            mode === key ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Icon />
        </button>
      ))}
    </fieldset>
  );
}

function ApiView({
  me,
  patch,
  goBilling,
}: {
  me: Me;
  patch: (fn: (m: Me) => Me) => void;
  goBilling: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [usage, setUsage] = useState<{ requests: number; bytes: number } | null>(null);
  const [usageErr, setUsageErr] = useState<string | null>(null);
  const key = me.keys?.[0];

  useEffect(() => {
    setUsage(null);
    setUsageErr(null);
    if (!key) return;
    // Say WHY there are no numbers. Every failure used to render as "warming
    // up", so a missing analytics token, a token without Account Analytics
    // Read, and a dataset that genuinely has no rows yet all looked like
    // "wait a bit" — and only one of those gets better on its own.
    fetch(`/portal/usage/${key.key}`)
      .then(async (r) => {
        if (r.ok) {
          setUsage(await r.json());
          return;
        }
        const body = (await r.json().catch(() => null)) as { error?: string } | null;
        setUsageErr(
          r.status === 501
            ? "Usage reporting is not configured — set CF_ACCOUNT_ID and CF_ANALYTICS_TOKEN."
            : (body?.error ?? `Usage unavailable (${r.status}).`),
        );
      })
      .catch(() => setUsageErr("Usage request failed."));
  }, [key?.key]);

  const create = async () => {
    setBusy(true);
    setErr(null);
    const r = await fetch("/portal/create", { method: "POST" });
    const body = await r.json().catch(() => ({}));
    setBusy(false);
    if (r.ok && body.key)
      patch((m) => ({
        ...m,
        keys: [...(m.keys ?? []), { key: body.key, expires: body.expires, plan: body.plan }],
      }));
    else setErr(body.error ?? `Could not create a key (${r.status}) — try again in a moment.`);
  };

  return (
    <div className="flex flex-col gap-10">
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-2xl">API</h1>
        {me.plan !== "pro" && (
          <button
            className="inline-flex h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-sm border border-brand/60 px-4 text-sm font-medium text-brand transition-colors hover:bg-brand hover:text-background"
            onClick={goBilling}
          >
            <span aria-hidden="true" className="text-lg leading-none">✦</span> Upgrade
          </button>
        )}
      </div>

      {/* What the key has actually done, at the top where it is asked for.
          It lived under the key list as one line of small text, which is not
          where anyone looks to answer "how much have I used". */}
      {key && (
        <section className="rounded-sm border border-border bg-card p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-base">Usage · last 30 days</h2>
              <p className="text-[13px] text-muted-foreground">
                {key.name ?? key.key.slice(0, 12)} · {key.plan === "pro" ? "Pro" : "Free"}
              </p>
            </div>
            {usage ? (
              <div className="flex items-end gap-8">
                <div className="flex flex-col">
                  <span className="text-2xl tabular-nums">{usage.requests.toLocaleString()}</span>
                  <span className="text-[13px] text-muted-foreground">
                    requests
                    {key.plan === "pro" && ` of ${PRO_REQUESTS.toLocaleString()} included`}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-2xl tabular-nums">{(usage.bytes / 1e9).toFixed(2)}</span>
                  <span className="text-[13px] text-muted-foreground">GB transferred</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {usageErr ?? "Warming up — samples appear a minute after the first request."}
              </p>
            )}
          </div>
          {/* Pro has a number to be a fraction OF. Free is a rate limit, and a
              bar against nothing would invent a quota that does not exist. */}
          {usage && key.plan === "pro" && (
            <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-border">
              <div
                className="h-full bg-brand transition-[width]"
                style={{ width: `${Math.min(100, (usage.requests / PRO_REQUESTS) * 100).toFixed(1)}%` }}
              />
            </div>
          )}
          {usage && key.plan !== "pro" && (
            <p className="mt-4 text-[13px] text-muted-foreground">
              The free plan is a rate limit, about 10 requests a second — not a monthly allowance.
            </p>
          )}
        </section>
      )}

      {key && (
        <section className="rounded-sm border border-border bg-card p-6">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex max-w-[34ch] flex-col gap-3">
              <h2 className="text-base">Quick start</h2>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Paste into <code className="text-foreground">~/.config/trenches/config.json</code> — or merge the
                networks block into yours.
              </p>
            </div>
            <div className="w-full lg:max-w-[55%]">
              <EndpointTabs k={key.key} />
            </div>
          </div>
        </section>
      )}

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base">API Keys</h2>
          <div className="flex items-center gap-2">
            {/* Just the keys' own action — the sell lives in Billing now. */}
            <button
              className={BTN_PRIMARY}
              disabled={busy || (me.keys?.length ?? 0) >= (me.plan === "pro" ? 3 : 1)}
              onClick={create}
            >
              + Create key
            </button>
          </div>
        </div>
        <div className="rounded-sm border border-border">
          <div className="hidden grid-cols-[1.4fr_1.2fr_0.8fr_5rem] gap-4 rounded-t-sm border-b border-border bg-card px-4 py-2.5 text-xs text-muted-foreground sm:grid">
            <span>Name</span>
            <span>Key</span>
            <span>Plan</span>
            <span />
          </div>
          {me.keys?.length ? (
            me.keys.map((k) => <KeyRow key={k.key} k={k} me={me} patch={patch} />)
          ) : (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              No keys yet — create one and your endpoints appear here.
            </p>
          )}
        </div>
        {err && <p className="text-sm text-bad">{err}</p>}
        {key && (
          <p className="text-[13px] text-muted-foreground">
            {usage
              ? `Last 30 days: ${usage.requests.toLocaleString()} requests · ${(usage.bytes / 1e9).toFixed(2)} GB`
              : (usageErr ?? "Usage reporting warming up — samples appear a minute after the first request.")}
            {key.plan === "free" && (
              <>
                {" "}
                <button className="cursor-pointer text-brand hover:underline underline-offset-4" onClick={goBilling}>
                  Pro is $119/mo — 10M requests
                </button>
              </>
            )}
          </p>
        )}
      </section>
    </div>
  );
}

/** A footer that knows its place: the copyright, the legal three, the
 *  theme switch, the disclaimer. Navigation lives in the rail; the rest
 *  is mentioned where it matters, in Support. */
function PortalFooter() {
  return (
    <footer className="border-t border-border px-6 py-6 lg:px-10">
      <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4">
          <p className="m-0 text-[13px] text-muted-foreground">© 2026 Trenches.sh</p>
          <div className="flex items-center gap-5 text-[13px]">
            {[
              { label: "Terms", href: "https://trenches.sh/terms" },
              { label: "Privacy", href: "https://trenches.sh/privacy" },
              { label: "Licence", href: "https://trenches.sh/license" },
            ].map((l) => (
              <a
                key={l.label}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground transition-colors hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
          </div>
          <ThemeToggle />
      </div>
    </footer>
  );
}

function Console({ me, patch }: { me: Me; patch: (fn: (m: Me) => Me) => void }) {
  const [view, setView] = useState<View>("api");
  const [menu, setMenu] = useState(false);
  // The site's sliding highlight: one marker that travels, not four borders
  // switching on and off — that is what makes the movement continuous.
  const items = useRef<(HTMLButtonElement | null)[]>([]);
  const [marker, setMarker] = useState({ top: 0, height: 0 });
  useEffect(() => {
    const el = items.current[NAV.findIndex((n) => n.id === view)];
    if (el) setMarker({ top: el.offsetTop, height: el.offsetHeight });
  }, [view]);
  const go = (v: View) => {
    setView(v);
    setMenu(false);
    scrollTo({ top: 0 });
  };

  return (
    <>
      <Gutters />
      <div className="mx-auto flex min-h-svh w-full max-w-[1388px] flex-col lg:flex-row">
      {/* The rail the main site uses: same width, same border, same posture —
          the brand at the top, the pages in the middle, you at the bottom. */}
      <nav
        aria-label="Console"
        className="sticky top-0 hidden h-svh w-[236px] shrink-0 flex-col justify-between self-start border-r border-border py-6 lg:flex"
      >
        <div className="flex min-h-0 flex-col gap-7">
          <span className="pr-6 font-display text-[1.05rem] whitespace-nowrap">
            <span className="text-brand">▚ </span>Trenches <span className="text-muted-foreground">RPC</span>
            {me.plan === "pro" && <sup className="ml-1 text-[0.6em] text-brand">Pro</sup>}
          </span>
          <div className="relative">
            <span
              aria-hidden="true"
              className="absolute left-0 w-full border-l-2 border-brand bg-foreground/5 transition-all duration-300"
              style={{ top: marker.top, height: marker.height }}
            />
            {NAV.map((n, i) => (
              <button
                key={n.id}
                ref={(el) => {
                  items.current[i] = el;
                }}
                onClick={() => go(n.id)}
                aria-current={view === n.id ? "page" : undefined}
                className={`relative flex w-full cursor-pointer items-center py-2 pr-6 pl-[22px] text-left text-sm transition-colors duration-300 ${
                  view === n.id ? "font-semibold text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {n.title}
              </button>
            ))}
          </div>
        </div>
        <button
          className="mx-4 flex cursor-pointer items-center justify-center rounded-sm px-3 py-2 text-sm text-bad transition-colors hover:bg-brand hover:text-background"
          onClick={() => (location.href = "/auth/logout")}
        >
          Sign out
        </button>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Below lg the rail folds into the site's sheet-under-header. */}
        <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur lg:hidden">
          <div className="flex h-14 items-center justify-between gap-4 px-6">
            <span className="font-display text-[1.05rem] whitespace-nowrap">
              <span className="text-brand">▚ </span>Trenches <span className="text-muted-foreground">RPC</span>
              {me.plan === "pro" && <sup className="ml-1 text-[0.6em] text-brand">Pro</sup>}
            </span>
            <button
              type="button"
              onClick={() => setMenu(!menu)}
              aria-expanded={menu}
              aria-label={menu ? "Close menu" : "Open menu"}
              className="-mr-2 cursor-pointer p-2 text-xl leading-none"
            >
              {menu ? "×" : "≡"}
            </button>
          </div>
          {menu && (
            <nav aria-label="Console" className="border-t border-border bg-background">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(n.id)}
                  aria-current={view === n.id ? "page" : undefined}
                  className={`block w-full cursor-pointer border-b border-border/50 px-6 py-3.5 text-left text-sm last:border-0 ${
                    view === n.id ? "text-brand" : "hover:text-brand"
                  }`}
                >
                  {n.title}
                </button>
              ))}
            </nav>
          )}
        </header>

        <main className="mx-auto w-full max-w-[1000px] flex-1 px-6 pt-10 pb-16 md:pt-14 lg:px-10">
          <div key={view} className="view-in">
            {view === "api" && <ApiView me={me} patch={patch} goBilling={() => go("billing")} />}
            {view === "docs" && <DocsView k={me.keys?.[0]?.key} />}
            {view === "agents" && <AgentsView k={me.keys?.[0]?.key} />}
            {view === "billing" && <BillingView me={me} goSupport={() => go("support")} />}
            {view === "support" && <SupportView />}
            {view === "account" && <AccountView me={me} patch={patch} />}
          </div>
        </main>

        <PortalFooter />
      </div>
    </div>
    </>
  );
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = () => {
    fetch("/portal/me")
      .then((r) => r.json())
      .then((m: Me) => setMe(m))
      .catch(() => setMe(null))
      .finally(() => setLoaded(true));
  };
  useEffect(refresh, []);

  // ?preview=dash renders the console with demo data — style without a session.
  const patch = (fn: (m: Me) => Me) => setMe((m) => (m ? fn(m) : m));

  if (new URLSearchParams(location.search).get("preview") === "dash") {
    const demo: Me = {
      providers: { github: true, google: true },
      who: "msakiart",
      email: "meek.dev3@gmail.com",
      provider: "google",
      plan: "pro",
      billing: true,
      keys: [{ key: "trk_previewDemoKey12345678", plan: "free", expires: Math.floor(Date.now() / 1000) + 30 * 86_400 }],
    };
    return <Console me={demo} patch={() => {}} />;
  }

  if (!loaded) return null;
  if (me?.who) return <Console me={me} patch={patch} />;
  return <LoginPage me={me} />;
}

export default App;
